/**
 * @fileoverview Certificate preview card component
 */
import { useState, useEffect } from 'react';
import { CERTIFICATE_MODES } from '@/utils/constants';
import { formatDate } from '@/utils/formatters';
import { useLanguage } from '@/contexts/LanguageContext';

export const CertificatePreview = ({ formData, mode, beneficiary }) => {
  const { t } = useLanguage();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load templates from API
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch('/api/templates', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await response.json();
        // API returns array directly, not { templates: [...] }
        const templateList = Array.isArray(data) ? data : (data.templates || []);
        setTemplates(templateList);
        // Select first template by default
        if (templateList.length > 0) {
          setSelectedTemplate(templateList[0]);
        }
      } catch (error) {
        console.error('Error loading templates:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  const modeConfig = CERTIFICATE_MODES[mode];
  const isMaintenance = mode === 'mro';
  const maintenanceData = isMaintenance ? formData : null;
  const pilotData = !isMaintenance ? formData : null;

  // Get beneficiary name - use beneficiary if available, otherwise fall back to formData
  const beneficiaryName = beneficiary?.fullName || pilotData?.beneficiaryName || t('preview.student_name', 'Nombre del Alumno');

  // Get template image path
  const templateImagePath = selectedTemplate?.file_path
    ? `/uploads/${selectedTemplate.file_path.replace(/\\/g, '/')}`
    : '/uploads/templates/caccaa50-d12e-4f98-908e-4a4074e882e4/sencillo.svg';

  return (
    <div className="preview-panel">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-blue-600 flex items-center gap-2">
          <i className="fas fa-eye"></i> {t('preview.title', 'Vista Previa del Certificado')}
        </h3>

        {/* Template Selector */}
        <div className="flex items-center gap-2">
          <label htmlFor="template-select" className="text-sm font-medium text-slate-700">
            {t('preview.template', 'Plantilla')}:
          </label>
          <select
            id="template-select"
            value={selectedTemplate?.id || ''}
            onChange={(e) => {
              const template = templates.find(t => t.id === e.target.value);
              setSelectedTemplate(template);
            }}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            disabled={loading || templates.length === 0}
          >
            {loading ? (
              <option>{t('common.loading', 'Cargando...')}</option>
            ) : templates.length === 0 ? (
              <option>{t('templates.empty_title', 'No hay plantillas')}</option>
            ) : (
              templates.map(template => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))
            )}
          </select>
        </div>
      </div>

      <div className="bg-white p-8 border-4 border-double border-slate-200 rounded-lg text-center relative overflow-hidden aspect-[1.414/1] flex flex-col items-center justify-center shadow-xl">
        {/* Background SVG Template */}
        {selectedTemplate ? (
          <img
            src={templateImagePath}
            className="absolute inset-0 w-full h-full object-contain z-0"
            alt=""
            onError={(e) => {
              console.error('Failed to load template:', templateImagePath);
              e.target.src = '/templates/certificate-template.svg';
            }}
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 bg-slate-50 p-6 text-center">
            <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mb-6 shadow-sm border border-slate-200">
              <i className="fas fa-file-image text-3xl text-slate-300"></i>
            </div>
            <span className="text-2xl font-bold text-slate-600">
              {t('preview.select_template', 'Seleccione la plantilla del certificado')}
            </span>
            <p className="text-sm text-slate-400 mt-2">
              {t('preview.select_template_desc', 'Seleccione una plantilla disponible para visualizar el diseño final')}
            </p>
          </div>
        )}

        <div className="relative z-10 w-full px-8">
          <div className="flex flex-col items-center mb-4">
            <div className="w-16 h-16 bg-blue-50/50 rounded-full flex items-center justify-center mb-4 border border-blue-100">
              <i className={isMaintenance ? "fas fa-wrench text-2xl text-blue-600" : "fas fa-award text-2xl text-blue-600"}></i>
            </div>
            <h2 className="text-2xl font-bold text-slate-800 uppercase tracking-tighter">
              {isMaintenance ? t('preview.maint_certificate', 'Certificado de Mantenimiento') : 'AeroCert Blockchain Diploma'}
            </h2>
            <div className="h-1 w-20 bg-blue-600 mt-2 rounded-full"></div>
          </div>

          <div className="my-6">
            <p className="text-[10px] text-slate-500 italic mb-1 uppercase tracking-widest">
              {isMaintenance ? t('preview.for_aircraft', 'Para la aeronave:') : t('preview.awarded_to', 'Se otorga con honor a:')}
            </p>
            <p className="text-2xl font-black text-slate-900 border-b-2 border-slate-200 inline-block px-8 pb-1 mb-1">
              {isMaintenance
                ? (maintenanceData?.aircraftModel || t('cert_form.aircraft_model', 'Modelo de Aeronave'))
                : beneficiaryName}
            </p>
            <p className="text-[10px] text-slate-400 font-medium">
              {isMaintenance
                ? `${t('cert_form.registration', 'Matrícula')}: ${maintenanceData?.registration || 'LV-XXXX'}`
                : `ID: ${pilotData?.idOrLicense || t('cert_form.id_license', 'DNI / Licencia')}`}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4 max-w-md mx-auto text-left mb-6">
            <div className="bg-white/50 backdrop-blur-[1px] p-2 rounded border border-slate-100 shadow-sm">
              <p className="text-[7px] text-slate-400 uppercase font-bold">{t('history.document_type', 'Tipo de Documento')}</p>
              <p className="text-[10px] font-bold text-slate-800">
                {isMaintenance ? maintenanceData?.maintenanceType : pilotData?.certificateType || t('preview.official_cert', 'Certificación Oficial')}
              </p>
            </div>
            <div className="bg-white/50 backdrop-blur-[1px] p-2 rounded border border-slate-100 shadow-sm">
              <p className="text-[7px] text-slate-400 uppercase font-bold">{t('cert_form.issue_date', 'Fecha de Emisión')}</p>
              <p className="text-[10px] font-bold text-slate-800">
                {formatDate(formData?.issueDate || new Date().toISOString())}
              </p>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-slate-100 flex justify-between items-end">
            <div className="text-left">
              <p className="text-[8px] font-black text-[#192A42] uppercase tracking-widest mb-1">AereoCert Validation System</p>
              <div className="flex gap-4">
                <div className="text-[7px] text-slate-400">
                  <p className="font-bold">STATUS</p>
                  <p className="text-orange-500">{t('preview.pending_signature', 'PENDIENTE DE FIRMA')}</p>
                </div>
                <div className="text-[7px] text-slate-400 border-l border-slate-200 pl-4">
                  <p className="font-bold">NETWORK</p>
                  <p>POLYGON BLOCKCHAIN</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="text-right mr-2 invisible md:visible">
                <p className="text-[7px] font-bold text-slate-400">SIGNATURE ID</p>
                <p className="text-[8px] font-mono text-slate-300">0x...7A42</p>
              </div>
              <div className="bg-white p-1 rounded-md border border-slate-200 shadow-sm">
                <div className="w-12 h-12 bg-slate-50 flex items-center justify-center text-[10px] font-bold text-slate-300 border border-dashed border-slate-200">QR</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 p-4 bg-blue-50/50 rounded-xl border border-blue-100 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
          <i className="fas fa-info-circle text-blue-600"></i>
        </div>
        <p className="text-xs text-blue-700 leading-relaxed font-medium">
          <strong>{t('preview.preview_mode', 'Modo Vista Previa')}:</strong> {t('preview.preview_note', 'Los datos mostrados arriba se reflejarán exactamente en el certificado final una vez firmado en la blockchain.')}
        </p>
      </div>
    </div>
  );
};
